/** 上传时生成静态小图；档位仍沿用客户端正在使用的规格。 */
export const STATIC_THUMB_WIDTHS = [160, 320, 640, 1152] as const;
export type StaticThumbWidth = typeof STATIC_THUMB_WIDTHS[number];
export type StaticThumbUrls = Record<StaticThumbWidth, string>;
export const THUMB_MAX_PIXELS = 4_000_000;
// 沿用既有上传 50MB 上限；仅 Worker 内解码另限像素，浏览器可处理更大的原图。
export const THUMB_MAX_BYTES = 50 * 1024 * 1024;

/**
 * 小图一律是有损 WebP（透明通道保留）。Idol Ludo（Cocos 3.8.8）与 Fast Ludo（Unity + WebP 插件）都按文件头解码；
 * 同画质下比 JPEG 小约三成、比透明 PNG 小约六倍（2026-11-04 抽 60 张 640 档实测）。
 */
export const THUMB_WEBP_QUALITY = 80;

/** 有损 WebP：RIFF 里有 VP8 块。无损（VP8L）体积接近 PNG，不收。 */
export function isLossyWebp(b: Uint8Array): boolean {
  if (b.length < 20 || String.fromCharCode(...b.subarray(0, 4)) !== "RIFF" || String.fromCharCode(...b.subarray(8, 12)) !== "WEBP") return false;
  for (let at = 12; at + 8 <= b.length;) {
    const fourcc = String.fromCharCode(...b.subarray(at, at + 4));
    if (fourcc === "VP8 ") return true;
    if (fourcc === "VP8L") return false;
    const size = (b[at + 4] | (b[at + 5] << 8) | (b[at + 6] << 16) | (b[at + 7] << 24)) >>> 0;
    at += 8 + size + (size & 1);
  }
  return false;
}

export function thumbDimensions(width: number, height: number, target: number) {
  const w = Math.min(width, target);
  return { width: w, height: Math.max(1, Math.round(height * w / width)) };
}

/** 所有回源与跳转都只接受图片 CDN；不接受任意外链、凭据、端口或现场缩图 URL。 */
export function canonicalThumbSource(raw: string): string | null {
  if (raw.length > 2048) return null;
  try {
    const u = new URL(raw);
    if (u.href.length > 2048 || u.protocol !== "https:" || u.username || u.password || u.port ||
      !["pbtest.buyacard.cc", "resource.bingo84.com"].includes(u.hostname) ||
      !/^\/(pb|pp)\//.test(u.pathname) || u.search || u.hash) return null;
    if (u.hostname === "pbtest.buyacard.cc" && !u.pathname.startsWith("/pb/")) return null;
    if (u.hostname === "resource.bingo84.com" && !u.pathname.startsWith("/pp/")) return null;
    return u.href;
  } catch { return null; }
}

export function encodeThumbSource(source: string): string {
  return btoa(source).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

function decodeThumbSource(token: string): string | null {
  if (token.length > Math.ceil(2048 * 4 / 3) || !/^[A-Za-z0-9_-]+$/.test(token)) return null;
  try {
    const source = atob(token.replaceAll("-", "+").replaceAll("_", "/"));
    return source.length <= 2048 && encodeThumbSource(source) === token ? source : null;
  } catch { return null; }
}

export function parseThumbnailRequest(url: URL, validateSource = canonicalThumbSource): {
  width: StaticThumbWidth;
  source: string;
} | null {
  const path = /^\/media\/(?:thumb\/v1|thumb-fallback)\/([^/]+)(?:\/([^/]+))?$/.exec(url.pathname);
  if (!path) return null;
  const width = STATIC_THUMB_WIDTHS.find(value => value === Number(path[1]));
  if (!width) return null;
  if (path[2] !== undefined) {
    const raw = decodeThumbSource(path[2]);
    const source = raw === null ? null : validateSource(raw);
    return source ? { width, source } : null;
  }
  const raw = url.searchParams.get("src") ?? "";
  const source = validateSource(raw);
  if (source) return { width, source };
  // Cocos iOS 会把查询中已有的百分号再编码一次。合法 URL 中的转义保持不变。
  if (!/^https%3a%2f%2f/i.test(raw)) return null;
  try {
    const decoded = validateSource(decodeURIComponent(raw));
    return decoded ? { width, source: decoded } : null;
  } catch { return null; }
}

export async function thumbSourceHash(source: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(source));
  return [...new Uint8Array(digest)].map(v => v.toString(16).padStart(2, "0")).join("");
}
